import { UpdateUserDto, UserDto, UserType } from '../../../../api-client';
import { ErrorResponse } from '../../../../common/types/errorRespose';
import React from 'react';
import { Box, Button, Flexbox, TextField, Typography } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../common/components/ErrorBox/ErrorBox';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { CommonMessages } from '../../../../common';
import { EditUserInfoFormValues, useEditUserInfoForm } from '../../hooks/useEditUserInfoForm';
import styled from 'styled-components';
import { Divider } from '../../../Profile/ui/GeneralInfoForm/GeneralInfoForm';

export type UpdateUserVariables = {
  updateUserDto: UpdateUserDto;
  userId: string;
};

type EditUserInfoFormProps = {
  user: UserDto;
  isUpdating: boolean;
  error: ErrorResponse | null;
  updateUser: (updateUserDto: UpdateUserVariables) => void;
};

export type EditUserInfoFormComponent = React.FC<EditUserInfoFormProps>;

const EditUserInfoFormWrapper = styled(Flexbox)`
  padding: ${({ theme }) => theme.spacing(4)}px;
  background: #fff;
  border-radius: 4px;
  overflow-y: auto;
`;

export const EditUserInfoForm: EditUserInfoFormComponent = ({
  user,
  error,
  isUpdating,
  updateUser,
}) => {
  const intl = useIntl();
  const { control, formState, handleSubmit } = useEditUserInfoForm(user);

  const onSubmit = ({ name, email, operatorId }: EditUserInfoFormValues) => {
    updateUser({
      updateUserDto: {
        name,
        email,
        ...(user.type === UserType.Standalone ? { operatorId: operatorId || null } : {}),
      },
      userId: user.id,
    });
  };

  return (
    <EditUserInfoFormWrapper container direction="column" item width="100%">
      <Typography variant="body1">General information</Typography>
      <Box my={4}>
        <Divider />
      </Box>
      <form style={{ width: '100%' }}>
        {error && <ErrorBox error={error} />}
        <Flexbox direction="column" container gutter={4}>
          <Flexbox item>
            <Controller
              name="email"
              defaultValue={user.email}
              control={control}
              render={({ field: { ref, value, onChange } }) => (
                <TextField
                  fullWidth
                  id="hii-current-email"
                  autoComplete="email"
                  name="email"
                  disabled={isUpdating}
                  type="text"
                  ref={ref}
                  label={intl.formatMessage(CommonMessages.email)}
                  value={value}
                  onChange={onChange}
                  error={formState.errors?.email?.message}
                />
              )}
            />
          </Flexbox>
          <Flexbox item>
            <Controller
              name="name"
              defaultValue={user.name}
              control={control}
              render={({ field: { ref, value, onChange } }) => (
                <TextField
                  fullWidth
                  id="hii-current-displayName"
                  label="Displayed name"
                  name="name"
                  disabled={isUpdating}
                  type="text"
                  autoComplete="name"
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  error={formState.errors?.name?.message}
                />
              )}
            />
          </Flexbox>
          {user.type === UserType.Standalone && (
            <Flexbox item>
              <Controller
                name="operatorId"
                defaultValue={user.operatorId ?? ''}
                control={control}
                render={({ field: { ref, value, onChange } }) => (
                  <TextField
                    fullWidth
                    id="hii-current-operatorId"
                    autoComplete="current-hii-operatorId"
                    label="Operator ID"
                    name="operatorId"
                    disabled={isUpdating}
                    type="text"
                    ref={ref}
                    value={value ?? ''}
                    onChange={onChange}
                    error={formState.errors?.operatorId?.message}
                  />
                )}
              />
            </Flexbox>
          )}
          <Flexbox item container justifyContent="flex-end">
            <Button
              onClick={handleSubmit(onSubmit)}
              type="submit"
              disabled={
                (formState?.errors && !!Object.entries(formState?.errors).length) ||
                Object.keys(formState?.dirtyFields).length < 1 ||
                isUpdating
              }
            >
              {isUpdating ? 'Updating ...' : 'Update'}
            </Button>
          </Flexbox>
        </Flexbox>
      </form>
    </EditUserInfoFormWrapper>
  );
};
